/* eslint-disable indent */
import './styles.scss';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useForm, useFormState } from 'react-hook-form';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import {
  downloadScientificProfileFile,
  getAllOrganizations,
  getAllProjectRoles,
  getGeneralInfoById,
} from 'apis/projectProposals.api';
import { toast } from 'layout';
import { ADJUSTMENT_COMMENTS, PROJECT_ROLES, TOAST } from 'constant';
import { getByEmail as getContactByEmail } from 'apis/contacts.api';

import {
  getAcadamicLevelShortHand,
  mapError,
  modifyBindingProjectProposalResearchPersonnelData,
  modifyProjectProposalResearchPersonnelUpdateData,
  toNonAccentVietnamese,
} from 'utils/func';
import { createPersonnelAdjustment, editPersonnelAdjustment } from 'apis/personnelAdjustment.api';

import Loading from 'components/Loading';
import ErrorDialog from 'components/ErrorDialog';
import {
  HeaderInfo,
  PreResearchersTable,
} from 'features/Project/components/EditPersonnelAdjustment';
import PropTypes from 'prop-types';
import {
  useGetPersonnelAdjustmentById,
  useSubmitPersonnelAdjustment,
} from 'utils/hooks/personnelAdjustments';
import { Message } from 'primereact/message';
import ReplyAdjustmentCommentModal from 'features/Project/components/ReplyAdjustmentCommentModal';
import { useGetLastAdjustmentComments } from 'utils/hooks/adjustmentComments';
import ResearchersTable from './ResearchersTable';
import DownloadPersonnelAdjustmentFile from './DownloadPersonnelAdjustmentFile';

function CreateOrEditPersonnelAdjustment({ isEdit, readOnly }) {
  const { personnelAdjustmentId } = useParams();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('projectId');
  const projectProposalId = searchParams.get('projectProposalId');
  const { t } = useTranslation();
  const location = useLocation();
  const { isRequestedEdit } = location.state || {};
  const navigate = useNavigate();
  const [isVisibleErrorDialog, setIsVisibleDialogError] = useState(false);
  const [isVisibleSubmitDialog, setIsVisibleSubmitDialog] = useState(false);
  const [isSubmitReviseModalOpen, setIsSubmitReviseModalOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isEditMode, setIsEditMode] = useState(isEdit);
  const [personnelAdjustmentIdUsed, setPersonnelAdjustmentIdUsed] = useState(
    personnelAdjustmentId || null
  );
  const [oldPPContacts, setOldPPContacts] = useState([]);
  const [oldMembersOriginal, setOldMembersOriginal] = useState([]);
  const [emailList, setEmailList] = useState([]);

  const {
    mutateAsync: createPersonnelAdjustmentAsync,
    error: errorResCreate,
    isError: isErrorResCreate,
    isLoading: isLoadingCreatePersonnelAdjustment,
  } = useMutation(createPersonnelAdjustment);

  const {
    mutateAsync: editPersonnelAdjustmentAsync,
    error: errorResEdit,
    isError: isErrorResEdit,
    isLoading: isLoadingEditPersonnelAdjustment,
  } = useMutation(editPersonnelAdjustment);
  const {
    data: generalInfoData,
    refetch,
    isLoading: isGeneralInfoLoading,
  } = useQuery(['projectProposalGeneralInfo', projectProposalId], () =>
    getGeneralInfoById(projectProposalId)
  );

  const proposalGeneralInfo = useMemo(() => generalInfoData?.data, [generalInfoData]);

  const { data: personnelAdjustment, refetch: refetchPersonnelAdjustment } =
    useGetPersonnelAdjustmentById(personnelAdjustmentIdUsed, {
      enabled: (isEditMode || readOnly) && !!personnelAdjustmentIdUsed,
    });

  const { mutateAsync, isLoading } = useSubmitPersonnelAdjustment();

  const { mutate: mutateDownloadScientificProfileFile, isLoading: isDowloadProfileLoading } =
    useMutation(downloadScientificProfileFile);

  const { control, handleSubmit, reset, watch, setValue } = useForm({
    mode: 'onChange',
    defaultValues: {},
    shouldFocusError: true,
  });
  const { errors, isDirty, dirtyFields } = useFormState({
    control,
  });

  const { mutate: bindContact } = useMutation(getContactByEmail);

  const { data: organizationsData, isLoading: isOrganizationLoading } = useQuery(
    ['projectProposalOrganizations'],
    getAllOrganizations,
    { staleTime: 1000 * 60 * 5 }
  );
  const { data: projectRolesData } = useQuery(['projectProposalProjectRoles'], getAllProjectRoles, {
    staleTime: 1000 * 60 * 5,
  });
  const organizations = useMemo(
    () =>
      organizationsData?.data.map((e) => ({
        ...e,
        name: t('lang') === 'vi' ? e?.name : e?.englishName,
      })) ?? [],
    [organizationsData, t]
  );

  const owner = useMemo(
    () =>
      proposalGeneralInfo?.projectProposalContacts?.find(
        (item) => item?.projectRole?.code === PROJECT_ROLES.OWNER
      ) ?? {},
    [proposalGeneralInfo]
  );

  const projectRoles = useMemo(() => projectRolesData?.data ?? [], [projectRolesData]);

  const { data: lastComment } = useGetLastAdjustmentComments(
    {
      adjustmentName: ADJUSTMENT_COMMENTS.PERSONNEL_ADJUSTMENT,
      adjustmentId: personnelAdjustmentId,
    },
    {
      enabled: !!isRequestedEdit,
    }
  );

  const handleBindingIntoContactFieldClick = (email, update, index, value) => {
    bindContact(
      { email },
      {
        onSuccess: (res) => {
          if (res?.data) {
            update(index, {
              ...value,
              email: res.data.email,
              contact: res.data,
              fullname: res?.data?.fullname,
              gender: res?.data?.scientificProfile?.gender ?? '',
              academicDegree: res?.data?.scientificProfile?.academicDegree ?? '',
              academicRank: res?.data?.scientificProfile?.academicRank ?? '',
              researchLines: res?.data?.scientificProfile?.researchLines ?? [],
              organizationId: res?.data?.organizationId,
              specificOrganizationName: res?.data?.specificOrganizationName,
            });
            toast(TOAST.SUCCESS, t('projectProposal.getDataSuccessMessage'));
          }
        },
        onError: () => {
          toast(TOAST.ERROR, t('projectProposal.getDataErrorMessage'));
        },
      }
    );
  };

  const handleDownloadScientificProfileFile = (
    contactId,
    contactName = '',
    academicRank = '',
    academicDegree = ''
  ) => {
    if (contactId && contactName) {
      mutateDownloadScientificProfileFile(contactId, {
        onSuccess: async (res) => {
          if (res?.data) {
            const file = new Blob([res.data]);

            const link = document.createElement('a');
            link.href = URL.createObjectURL(file);
            link.download = `${getAcadamicLevelShortHand(
              academicRank,
              academicDegree,
              t,
              true
            )}${toNonAccentVietnamese(contactName)}.pdf`;

            link.click();
          }
        },
        onError: () => {
          toast(
            TOAST.ERROR,
            t('projectProposal.general.researchHumanResources.downloadScientificProfileErrorTitle')
          );
        },
      });
    }
  };

  useEffect(() => {
    const data = isEditMode || readOnly ? personnelAdjustment : generalInfoData?.data;
    if (data && Object.keys(data).length) {
      let oldData = data;
      if (data?.oldMembers && !isEditMode && !readOnly) {
        oldData = !data?.oldMembers
          ? data
          : {
              ...data?.oldMembers?.oldOwner,
              projectProposalContacts: data?.oldMembers?.oldProjectProposalContacts,
            };
      } else if (isEditMode || readOnly) {
        oldData = {
          ...data?.oldMembers?.find((item) => item?.projectRole?.code === PROJECT_ROLES.OWNER),
          projectProposalContacts: data?.oldMembers,
        };
      }
      let newData = data;
      if (isEditMode || readOnly) {
        newData = {
          ...data?.newMembers?.find((item) => item?.projectRole?.code === PROJECT_ROLES.OWNER),
          projectProposalContacts: data?.newMembers,
        };
      }
      const preBindingGeneralData = modifyBindingProjectProposalResearchPersonnelData(oldData);
      setOldPPContacts(oldData?.projectProposalContacts?.map((ppContact) => ppContact.email) || []);
      setOldMembersOriginal(oldData?.projectProposalContacts || []);
      setEmailList(newData?.projectProposalContacts?.map((ppContact) => ppContact.email) || []);

      const bindingGeneralData = modifyBindingProjectProposalResearchPersonnelData(newData);
      // preGeneralData
      setValue('preGeneral', preBindingGeneralData);
      setValue('preGeneral.recommendedExperts', preBindingGeneralData.recommendedExperts);
      setValue('preGeneral.researchExpense', preBindingGeneralData.researchExpense);
      setValue('preGeneral.owner.researchLines', preBindingGeneralData?.owner?.researchLines ?? []);
      setValue('preGeneral.partners', preBindingGeneralData.partners);
      setValue('preGeneral.scientificSecretaries', preBindingGeneralData.scientificSecretaries);
      setValue('preGeneral.mainParticipants', preBindingGeneralData.mainParticipants);
      setValue('preGeneral.participants', preBindingGeneralData.participants);
      setValue('preGeneral.technicians', preBindingGeneralData.technicians);
      setValue('preGeneral.domesticExperts', preBindingGeneralData.domesticExperts);
      setValue('preGeneral.internationalExperts', preBindingGeneralData.internationalExperts);

      // generalData
      setValue('general', bindingGeneralData);
      setValue('general.recommendedExperts', bindingGeneralData.recommendedExperts);
      setValue('general.researchExpense', bindingGeneralData.researchExpense);
      setValue('general.owner.researchLines', bindingGeneralData?.owner?.researchLines ?? []);
      setValue('general.owners', [bindingGeneralData.owner]);
      setValue('general.partners', bindingGeneralData.partners);
      setValue('general.scientificSecretaries', bindingGeneralData.scientificSecretaries);
      setValue('general.mainParticipants', bindingGeneralData.mainParticipants);
      setValue('general.participants', bindingGeneralData.participants);
      setValue('general.technicians', bindingGeneralData.technicians);
      setValue('general.domesticExperts', bindingGeneralData.domesticExperts);
      setValue('general.internationalExperts', bindingGeneralData.internationalExperts);
    }
  }, [generalInfoData, personnelAdjustment, isEdit]);

  const onSubmit = async (_data) => {
    const { oldMembers, newMembers } = modifyProjectProposalResearchPersonnelUpdateData(
      _data,
      oldMembersOriginal
    );
    const dataSender = {
      newMembers,
      oldMembers,
      projectId,
    };

    if (!isEditMode) {
      const res = await createPersonnelAdjustmentAsync(dataSender);
      setIsEditMode(true);
      setPersonnelAdjustmentIdUsed(res?.data?.id);
      toast(TOAST.SUCCESS, t('editHumanResource.status.createSuccess'));
    } else {
      await editPersonnelAdjustmentAsync({
        personnelAdjustmentId,
        data: dataSender,
      });
      toast(TOAST.SUCCESS, t('editHumanResource.status.editSuccess'));
      refetchPersonnelAdjustment();
    }
    refetch();
    reset({}, { keepValues: true });
  };

  const handleSubmitPersonnelAdjustment = async () => {
    try {
      const res = await mutateAsync(personnelAdjustmentIdUsed);
      if (res.status.toString().startsWith('20')) {
        if (isRequestedEdit) {
          toast(TOAST.SUCCESS, t('editHumanResource.status.submitReviseSuccess'));
          setIsSubmitReviseModalOpen(false);
        } else {
          toast(TOAST.SUCCESS, t('editHumanResource.status.submitSuccess'));
          setIsVisibleSubmitDialog(false);
        }
        setIsSubmitted(true);
      }
    } catch (error) {
      toast(TOAST.ERROR, mapError(t, error));
    }
  };

  const footerConfirmSubmit = () => (
    <div>
      <Button
        label={t('formLayout.action.cancel')}
        className="w-7rem"
        severity="danger"
        onClick={() => setIsVisibleSubmitDialog(false)}
        autoFocus
      />
      <Button
        label={t('formLayout.action.ok')}
        className="w-7rem"
        severity="info"
        type="button"
        onClick={handleSubmitPersonnelAdjustment}
      />
    </div>
  );

  const formatLastComment = () => (
    <div>
      {t('projectAdjustment.commentFromSpecialist')}
      {': '}
      {lastComment?.comment}
    </div>
  );

  return (
    <>
      {(isLoadingEditPersonnelAdjustment ||
        isLoadingCreatePersonnelAdjustment ||
        isOrganizationLoading ||
        isDowloadProfileLoading ||
        isGeneralInfoLoading) && <Loading />}
      {(isErrorResCreate || isErrorResEdit) && (
        <ErrorDialog
          title={t('editHumanResource.action.edit')}
          isError={isErrorResCreate || isErrorResEdit}
          error={errorResCreate || errorResEdit}
          visible={isVisibleErrorDialog}
          setVisible={setIsVisibleDialogError}
        />
      )}

      <Dialog
        header={<p className="text-blue-500">{t('projectProposal.confirmationTitle')}</p>}
        visible={isVisibleSubmitDialog}
        position="center"
        style={{ width: '400px' }}
        onHide={() => setIsVisibleSubmitDialog(false)}
        draggable={false}
        footer={footerConfirmSubmit}
      >
        <Message
          className="mt-4 w-full"
          severity="warn"
          text={t('editHumanResource.confirmationMessage')}
        />
      </Dialog>
      <DownloadPersonnelAdjustmentFile id={personnelAdjustmentIdUsed} projectId={projectId} />
      <div className="p-5 edit-human-resource-form">
        <HeaderInfo
          projectProposalId={projectProposalId}
          isDownload={!!generalInfoData?.data?.oldMembers}
        />
        {lastComment && <Message severity="info" text={formatLastComment} />}

        <h5>{t('editHumanResource.proposal')}</h5>

        <div className="flex mb-1">
          <div className="mx-2 font-semibold">
            - {t('projectProposal.general.projectNameAndType.vietnameseName')}:
          </div>
          <div className="flex-1">{proposalGeneralInfo?.vietnameseName}</div>
        </div>
        <div className="flex mb-1">
          <div className="mx-2 font-semibold">
            - {t('projectProposal.general.projectNameAndType.englishName')}:{' '}
          </div>
          <div className="flex-1">{proposalGeneralInfo?.englishName}</div>
        </div>

        <div className="flex my-1 align-items-center">
          <div className="mx-2 font-semibold"> - {t('financialAdjustment.owner')} </div>
          <p className="flex-1">
            {getAcadamicLevelShortHand(owner?.academicRank, owner?.academicDegree, t, true)}
            <span>{owner?.fullname}</span>
          </p>
        </div>

        <h5>{t('editHumanResource.preHumanResource')}</h5>
        <PreResearchersTable
          organizations={organizations}
          projectRoles={projectRoles}
          watch={watch}
          control={control}
          errors={errors}
          handleDownloadScientificProfileFile={handleDownloadScientificProfileFile}
        />

        <form id="edit-humman-resource" onSubmit={handleSubmit(onSubmit)} autoComplete="off">
          <h5>{t('editHumanResource.postHumanResource')}</h5>
          <ResearchersTable
            organizations={organizations}
            projectRoles={projectRoles}
            watch={watch}
            control={control}
            errors={errors}
            onBindingIntoContactFieldClick={handleBindingIntoContactFieldClick}
            handleDownloadScientificProfileFile={handleDownloadScientificProfileFile}
            oldPPContacts={oldPPContacts}
            emailList={emailList}
            setEmailList={setEmailList}
            disabled={readOnly}
          />
          <div className="flex justify-content-end mt-4 gap-2">
            <Button
              label={t('extendImplementationTime.comeBack')}
              severity="danger"
              type="button"
              className="w-8rem"
              onClick={() => navigate(-1)}
            />
            {!readOnly && (
              <>
                <Button
                  label={t('formLayout.action.save')}
                  severity="info"
                  type="submit"
                  className="w-8rem"
                  //   onClick={() => {
                  //     if (Object.keys(errors || {}).length) {
                  //       toast(TOAST.ERROR, t('errorMessage.validationErrorMessage'));
                  //     }
                  //     // setIsVisibleSubmitDialog(true);
                  //   }}
                  disabled={
                    !(isDirty && Object.keys(dirtyFields).length) ||
                    isLoadingEditPersonnelAdjustment ||
                    isLoadingCreatePersonnelAdjustment ||
                    isSubmitted
                  }
                />
                {!isRequestedEdit ? (
                  <Button
                    label={t('formLayout.submit')}
                    severity="success"
                    type="button"
                    className="w-8rem"
                    onClick={() => {
                      if (Object.keys(errors || {}).length) {
                        toast(TOAST.ERROR, t('errorMessage.validationErrorMessage'));
                      }
                      setIsVisibleSubmitDialog(true);
                    }}
                    disabled={
                      (isDirty && Object.keys(dirtyFields).length) ||
                      !Object.keys(personnelAdjustment).length ||
                      isLoadingEditPersonnelAdjustment ||
                      isLoadingCreatePersonnelAdjustment ||
                      isSubmitted
                    }
                  />
                ) : (
                  <Button
                    label={t('extendImplementationTime.revise')}
                    severity="warning"
                    type="button"
                    className="w-12rem"
                    onClick={() => {
                      if (Object.keys(errors || {}).length) {
                        toast(TOAST.ERROR, t('errorMessage.validationErrorMessage'));
                      }
                      setIsSubmitReviseModalOpen(true);
                    }}
                    disabled={
                      (isDirty && Object.keys(dirtyFields).length) ||
                      !Object.keys(personnelAdjustment).length ||
                      isLoadingEditPersonnelAdjustment ||
                      isLoadingCreatePersonnelAdjustment ||
                      isSubmitted
                    }
                  />
                )}
              </>
            )}
          </div>
        </form>
      </div>
      <ReplyAdjustmentCommentModal
        visible={isSubmitReviseModalOpen}
        onClose={() => setIsSubmitReviseModalOpen(false)}
        adjustmentCommentId={lastComment?.id}
        onSubmit={handleSubmitPersonnelAdjustment}
        isSubmitting={isLoading}
        isRevise
      />
    </>
  );
}

CreateOrEditPersonnelAdjustment.propTypes = {
  isEdit: PropTypes.bool,
  readOnly: PropTypes.bool,
};

CreateOrEditPersonnelAdjustment.defaultProps = {
  isEdit: false,
  readOnly: false,
};

export default CreateOrEditPersonnelAdjustment;
